/**
 * Simulated live occupancy feed for exhibitor stands.
 *
 * The Mappedin Web SDK has no live footfall/occupancy sensor API — its
 * "Enterprise Data" surface (`EnterpriseLocation` / `EnterpriseCategory`)
 * is CMS-managed location metadata, not real-time crowd data. Per-venue
 * occupancy requires an external sensor/IoT feed the kiosk owner would
 * plug in. This service produces a deterministic, per-stand simulated
 * signal so the UI can be built and demoed now; swap `refresh()` for a
 * real WebSocket/SSE/poll handler later without touching call sites.
 */

export type OccupancyLevel = "low" | "moderate" | "busy";

export interface OccupancySnapshot {
  spaceId: string;
  level: OccupancyLevel;
  percent: number;
}

function hash01(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 1000) / 1000;
}

function levelFromPercent(percent: number): OccupancyLevel {
  if (percent < 40) return "low";
  if (percent < 75) return "moderate";
  return "busy";
}

export class OccupancyService {
  private snapshot = new Map<string, OccupancySnapshot>();
  private spaceIds: string[] = [];
  private tick = 0;
  private timer: number | undefined;
  private readonly listeners = new Set<(snapshot: Map<string, OccupancySnapshot>) => void>();

  /** Begins simulating occupancy for the given space ids, refreshing on `intervalMs`. */
  start(spaceIds: string[], intervalMs = 20_000): void {
    this.stop();
    this.spaceIds = spaceIds;
    this.refresh();
    this.timer = window.setInterval(() => this.refresh(), intervalMs);
  }

  stop(): void {
    if (this.timer !== undefined) window.clearInterval(this.timer);
    this.timer = undefined;
  }

  private refresh(): void {
    this.tick += 1;
    for (const id of this.spaceIds) {
      const base = hash01(id);
      const wobble = hash01(`${id}:${this.tick}`);
      const percent = Math.round((base * 0.65 + wobble * 0.35) * 100);
      this.snapshot.set(id, { spaceId: id, level: levelFromPercent(percent), percent });
    }
    for (const fn of this.listeners) fn(this.snapshot);
  }

  get(spaceId: string): OccupancySnapshot | undefined {
    return this.snapshot.get(spaceId);
  }

  onUpdate(fn: (snapshot: Map<string, OccupancySnapshot>) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
}
